/* ============================================================================
   Sign-in — "Request access" dialog.
   Reached from the login footer note (there is no open sign-up: accounts are
   admin-provisioned). Files a request for an administrator to review — it
   does not create an account or a session. Works while logged out: api.ts
   only attaches a bearer token when one exists, so an anonymous POST here is
   fine.

   Self-contained overlay (same visual pattern as modules/admin/Modal.tsx) so
   this module stays decoupled from the admin module — nothing outside
   modules/auth is imported or touched.
   ============================================================================ */

import { useEffect, useState, type FormEvent } from 'react';
import { apiPost, isApiError } from '../../lib/api';

interface Props {
  onClose: () => void;
}

// Fixed to the two self-service-requestable roles plus the default —
// Admin/SystemAdmin are never offered here; those are granted by an admin.
const REQUESTABLE_ROLES: { code: string; name: string }[] = [
  { code: 'Requester', name: 'Requester' },
  { code: 'Designer', name: 'Designer' },
  { code: 'Accounts', name: 'Accounts' },
];

export default function RequestAccessDialog({ onClose }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [requestedRoleCode, setRequestedRoleCode] = useState('Requester');
  const [justification, setJustification] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit() {
    setError(null);
    if (!name.trim() || !email.trim() || !justification.trim()) {
      setError('Name, work email and a short justification are all required.');
      return;
    }
    setSaving(true);
    try {
      await apiPost('/api/access-requests', {
        name: name.trim(),
        email: email.trim(),
        requestedRoleCode,
        justification: justification.trim(),
      });
      setSent(true);
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not submit the request.');
    } finally {
      setSaving(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!saving && !sent) submit();
  }

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(27, 36, 64, 0.45)',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={sent ? 'Request received' : 'Request access'}
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: '100%',
          maxWidth: 480,
          padding: 0,
          boxShadow: 'var(--shadow-lg)',
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className="card-header"
          style={{ padding: '18px 22px', margin: 0, borderBottom: '1px solid var(--line)' }}
        >
          <h3>{sent ? 'Request received' : 'Request access'}</h3>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Close"
            style={{ height: 30, padding: '0 10px' }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto' }}>
          {sent ? (
            <div className="stack">
              <p style={{ marginTop: 0 }}>
                <strong>Request received</strong> — an administrator will review it.
              </p>
              <p className="muted" style={{ marginBottom: 0 }}>
                You'll be contacted at <strong>{email.trim()}</strong> once your account is
                provisioned.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              {error && (
                <div className="error-text" style={{ marginBottom: 14 }}>
                  {error}
                </div>
              )}
              <div className="field">
                <label htmlFor="ra-name">Full name</label>
                <input
                  id="ra-name"
                  className="in"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  autoFocus
                />
              </div>
              <div className="field">
                <label htmlFor="ra-email">Work email</label>
                <input
                  id="ra-email"
                  className="in"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane.doe@uvahealth.org"
                />
              </div>
              <div className="field">
                <label htmlFor="ra-role">Role you're requesting</label>
                <select
                  id="ra-role"
                  value={requestedRoleCode}
                  onChange={(e) => setRequestedRoleCode(e.target.value)}
                >
                  {REQUESTABLE_ROLES.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 4 }}>
                <label htmlFor="ra-justification">Why do you need access?</label>
                <textarea
                  id="ra-justification"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Team, project, or requests you'll be submitting/handling…"
                  rows={4}
                />
              </div>
            </form>
          )}
        </div>

        <div
          className="row"
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--line)',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          {sent ? (
            <button className="btn btn-primary" onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={submit} disabled={saving}>
                {saving ? 'Sending…' : 'Send request'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
